import React from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { FormattedMessage } from 'react-intl';

import Checkbox from 'components/ui/Checkbox';
import AlertBox from 'components/ui/AlertBox';
import { productShape } from 'constants/propTypes';
import { DB_FEE_CODE } from 'constants/tariffCodes';

import { WRITE_OFF_REASONS } from 'constants/reasons';
import Product from '../Summary/Product';

import {
  Text,
  FeeItemWrapper,
  Container,
  CheckboxWrapper,
  ContentWrapper,
  ReasonSection,
  ReasonSelect,
  ReasonLabel,
  ExplanationLabel,
  ExplanationTextArea,
  CalculateRefund
} from './FeeItem.styles';

const FeeItem = ({
  feesSelected,
  setFeesSelected,
  fee,
  reasonCode,
  setReasonCode,
  label,
  setLabel,
  hasOptions,
  showAdditionalExplanation,
  additionalExplanation,
  canCalculateRefund,
  loading,
  formatMessage,
  setAdditionalExplanation,
  direction,
  disabled,
  disabledTooltipMessageId
}) => {
  const isDbFee = fee.code === DB_FEE_CODE;
  const isChecked = Boolean(feesSelected[`${fee.code}${direction}`]);
  const parentTooltipMessage = disabledTooltipMessageId && formatMessage ? formatMessage({ id: disabledTooltipMessageId }) : '';
  const dbFeeTooltipMessage = isDbFee && formatMessage ? formatMessage({ id: 'dbVoucher.issuedToCustomer' }) : '';
  const tooltipMessage = parentTooltipMessage || dbFeeTooltipMessage;
  const checkboxTooltipId = `other-fee-disabled-tooltip-${direction}-${fee.code}-${fee.itemRefs?.[0] || 'default'}`;

  return (
    <FeeItemWrapper>
      <Container>
        <CheckboxWrapper data-for={tooltipMessage ? checkboxTooltipId : undefined} data-tip={tooltipMessage || undefined}>
          <Checkbox
            dataCy="other-fee-checkbox"
            checked={isChecked}
            disabled={disabled || isDbFee}
            onChange={() => {
              const feeCode = isChecked ? null : fee.itemRefs;
              setFeesSelected({
                ...feesSelected,
                [`${fee.code}${direction}`]: feeCode
              });
            }}
          />
          {tooltipMessage && <ReactTooltip id={checkboxTooltipId} data-cy="other-fee-disabled-tooltip" />}
        </CheckboxWrapper>
        <ContentWrapper>
          <Product
            key={`${direction}${fee.code}`}
            product={fee}
            editable={isChecked}
            onChange={(quantity) =>
              setFeesSelected({
                ...feesSelected,
                [`${fee.code}${direction}`]: quantity ? fee.itemRefs.slice(0, quantity) : []
              })
            }
          />
          {isDbFee && (
            <AlertBox variant={AlertBox.variants.WARNING} dataCy="db-fee-warning">
              <FormattedMessage id="dbVoucher.issuedToCustomer" />
            </AlertBox>
          )}
        </ContentWrapper>
      </Container>
      {hasOptions && isChecked && (
        <>
          <ReasonSection>
            <ReasonLabel>
              <Text>{formatMessage({ id: 'paymentsTab.reason' })}</Text>

              <ReasonSelect
                value={`${reasonCode}_${label}`}
                required
                defaultValue=""
                data-cy="other-fees-reason"
                onChange={(event) => {
                  const [code, label] = event.target.value.split('_');
                  setReasonCode(code);
                  setLabel(label);
                  setAdditionalExplanation('');
                }}
              >
                <option value="" disabled>
                  {formatMessage({
                    id: 'paymentsTab.defaultReason'
                  })}
                </option>
                {WRITE_OFF_REASONS.map(({ code, label }) => (
                  <option key={code} value={`${code}_${label}`}>
                    {label}
                  </option>
                ))}
              </ReasonSelect>
            </ReasonLabel>

            {showAdditionalExplanation && (
              <ExplanationLabel>
                <Text>
                  {formatMessage({
                    id: 'paymentsTab.additionalExplanation'
                  })}
                </Text>

                <ExplanationTextArea
                  value={additionalExplanation}
                  required
                  rows="1"
                  cols="40"
                  data-cy="other-fees-additional-explanation"
                  onChange={(event) => setAdditionalExplanation(event.target.value)}
                />
              </ExplanationLabel>
            )}
          </ReasonSection>
          <CalculateRefund data-cy="other-fees-calculate" disabled={!canCalculateRefund} loading={loading}>
            {formatMessage({ id: 'paymentsTab.calcRefund' })}
          </CalculateRefund>
        </>
      )}
    </FeeItemWrapper>
  );
};

FeeItem.propTypes = {
  feesSelected: PropTypes.shape({}),
  setFeesSelected: PropTypes.func,
  fee: productShape.isRequired,
  reasonCode: PropTypes.string,
  setReasonCode: PropTypes.func,
  label: PropTypes.string,
  setLabel: PropTypes.func,
  hasOptions: PropTypes.bool,
  showAdditionalExplanation: PropTypes.bool,
  additionalExplanation: PropTypes.string,
  canCalculateRefund: PropTypes.bool,
  loading: PropTypes.bool,
  formatMessage: PropTypes.func,
  setAdditionalExplanation: PropTypes.func,
  direction: PropTypes.string,
  disabled: PropTypes.bool,
  disabledTooltipMessageId: PropTypes.string
};
export default FeeItem;
